import { ThemeOptions } from '@material-ui/core/styles';

type SpriveThemeOptions = ThemeOptions & {
    contentWithSidebar?: {
        marginTop?: number;
    };
    content?: {
        marginTop?: number;
    };
    sidebar?: {
        width?: number;
        closedWidth?: number;
    };
};

// ---------------------------------------------------------------------------
// Theme system
//
// Every theme is a compact `ThemeSpec` describing only the colours that differ
// between themes. `buildTheme` expands a spec into the full MUI theme (palette
// + component overrides), and `buildCssVars` produces the sidebar CSS custom
// properties consumed by App.css. This keeps adding a new theme to a single
// spec object in `THEME_SPECS` below.
//
// The Sprive pink accent (#DA2972) is intentionally constant across themes so
// the brand reads the same everywhere; themes vary by surface/background/mode.
// ---------------------------------------------------------------------------

type ThemeMode = 'light' | 'dark';

interface ThemeSpec {
    name: string;
    label: string;
    mode: ThemeMode;
    // Surfaces
    bgDefault: string;
    bgPaper: string;
    bodyGradient: string;
    // Text
    textPrimary: string;
    textSecondary: string;
    // Shell
    sidebarBg: string;
    menuHeaderBg: string;
    appBarBg: string;
    // Accents
    secondary: string;
    // Small preview shown on the Configuration page
    swatch: { bg: string; surface: string; accent: string };
}

const ACCENT = '#DA2972';
const ACCENT_LIGHT = '#E55A91';
const ACCENT_DARK = '#B01D5A';
const BRAND_TITLE_LIGHT = '#DA2972';
const BRAND_TITLE_DARK = '#ff5c9d';

// hex (#rgb or #rrggbb) -> rgba() string
const rgba = (hex: string, alpha: number): string => {
    const h = hex.replace('#', '');
    const full = h.length === 3 ? h.split('').map((c) => c + c).join('') : h;
    const int = parseInt(full, 16);
    const r = (int >> 16) & 255;
    const g = (int >> 8) & 255;
    const b = int & 255;
    return `rgba(${r}, ${g}, ${b}, ${alpha})`;
};

const buildTheme = (spec: ThemeSpec): SpriveThemeOptions => {
    const isDark = spec.mode === 'dark';

    const neutralBorder = isDark ? 'rgba(255, 255, 255, 0.08)' : 'rgba(28, 30, 31, 0.08)';
    const menuLinkColor = isDark ? '#c7cfd2' : '#3f4a4e';
    const menuIconColor = isDark ? '#9aa8ac' : '#5b6a86';
    const listIconColor = isDark ? '#9aa8ac' : '#5f6f73';
    const paperShadow = isDark
        ? '0 8px 24px rgba(0, 0, 0, 0.4)'
        : '0 8px 24px rgba(28, 30, 31, 0.08)';
    const appBarText = isDark ? '#e8ebec' : '#1c1e1f';
    const tableHeadBg = rgba(ACCENT, isDark ? 0.14 : 0.06);
    const tableHeadColor = isDark ? '#F48FB6' : ACCENT;
    const tableBodyBorder = isDark ? 'rgba(255, 255, 255, 0.10)' : 'rgba(28, 30, 31, 0.08)';

    return {
        palette: {
            type: spec.mode,
            primary: {
                main: ACCENT,
                light: ACCENT_LIGHT,
                dark: ACCENT_DARK,
                contrastText: '#ffffff',
            },
            secondary: {
                main: spec.secondary,
            },
            background: {
                default: spec.bgDefault,
                paper: spec.bgPaper,
            },
            text: {
                primary: spec.textPrimary,
                secondary: spec.textSecondary,
            },
        },
        shape: {
            borderRadius: 14,
        },
        sidebar: {
            width: 280,
            closedWidth: 0,
        },
        typography: {
            fontFamily: '"Manrope", "Space Grotesk", "Segoe UI", sans-serif',
            h1: { fontFamily: '"Space Grotesk", "Manrope", sans-serif', fontWeight: 700 },
            h2: { fontFamily: '"Space Grotesk", "Manrope", sans-serif', fontWeight: 700 },
            h3: { fontFamily: '"Space Grotesk", "Manrope", sans-serif', fontWeight: 700 },
            h4: { fontFamily: '"Space Grotesk", "Manrope", sans-serif', fontWeight: 700 },
            h5: { fontFamily: '"Space Grotesk", "Manrope", sans-serif', fontWeight: 700 },
            h6: { fontFamily: '"Space Grotesk", "Manrope", sans-serif', fontWeight: 700 },
            subtitle1: { fontWeight: 600 },
            button: { textTransform: 'none', fontWeight: 600 },
        },
        contentWithSidebar: {
            marginTop: 32,
        },
        content: {
            marginTop: 32,
        },
        overrides: ({
            MuiCssBaseline: {
                '@global': {
                    body: {
                        backgroundColor: spec.bgDefault,
                        backgroundImage: spec.bodyGradient,
                        color: spec.textPrimary,
                    },
                    '#root': {
                        minHeight: '100vh',
                    },
                },
            },
            RaMenuItemLink: {
                root: {
                    borderRadius: 999,
                    margin: '4px 8px',
                    padding: '8px 14px',
                    border: '1px solid transparent',
                    borderLeft: '1px solid transparent',
                    transition: 'all 160ms ease',
                    color: menuLinkColor,
                    alignItems: 'center',
                    '& .MuiListItemText-root': {
                        margin: 0,
                    },
                    '& .MuiListItemText-primary': {
                        lineHeight: 1.35,
                    },
                    '& .MuiListItemIcon-root': {
                        color: menuIconColor,
                        marginTop: 0,
                        minWidth: 34,
                    },
                },
                active: {
                    border: '1px solid transparent',
                    borderLeft: '1px solid transparent',
                    backgroundColor: ACCENT,
                    color: '#ffffff',
                    boxShadow: `0 8px 18px ${rgba(ACCENT, isDark ? 0.4 : 0.28)}`,
                    '& .MuiListItemIcon-root': {
                        color: '#ffffff',
                    },
                    '& .menu-label': {
                        color: '#ffffff',
                    },
                },
            },
            MuiPaper: {
                elevation1: {
                    boxShadow: paperShadow,
                },
                root: {
                    border: `1px solid ${neutralBorder}`,
                    backgroundClip: 'padding-box',
                },
            },
            MuiCard: {
                root: {
                    borderRadius: 18,
                },
            },
            MuiAppBar: {
                colorSecondary: {
                    color: appBarText,
                    backgroundColor: spec.appBarBg,
                    backdropFilter: 'blur(12px)',
                    borderBottom: `1px solid ${neutralBorder}`,
                },
            },
            MuiToolbar: {
                regular: {
                    minHeight: 72,
                },
            },
            MuiDrawer: {
                paper: {
                    backgroundColor: spec.sidebarBg,
                    borderRight: `1px solid ${neutralBorder}`,
                },
            },
            MuiListItemIcon: {
                root: {
                    minWidth: 40,
                    color: listIconColor,
                },
            },
            MuiButton: {
                contained: {
                    backgroundColor: ACCENT,
                    color: '#ffffff',
                    boxShadow: `0 10px 22px ${rgba(ACCENT, isDark ? 0.3 : 0.2)}`,
                },
                containedSecondary: {
                    backgroundColor: spec.secondary,
                    color: isDark ? '#1f1308' : '#1f1308',
                },
                outlined: {
                    borderColor: rgba(ACCENT, isDark ? 0.5 : 0.4),
                },
            },
            MuiInputBase: {
                root: {
                    borderRadius: 12,
                },
            },
            MuiOutlinedInput: {
                root: {
                    borderRadius: 12,
                    backgroundColor: isDark ? 'rgba(255, 255, 255, 0.04)' : '#ffffff',
                    '&:hover .MuiOutlinedInput-notchedOutline': {
                        borderColor: ACCENT,
                    },
                    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                        borderColor: ACCENT,
                        borderWidth: 2,
                    },
                },
            },
            MuiFormLabel: {
                root: {
                    maxWidth: 'calc(100% - 24px)',
                    whiteSpace: 'normal',
                    lineHeight: 1.2,
                },
            },
            MuiInputLabel: {
                root: {
                    maxWidth: 'calc(100% - 24px)',
                    whiteSpace: 'normal',
                    lineHeight: 1.2,
                },
                shrink: {
                    backgroundColor: spec.bgDefault,
                    padding: '0 6px',
                },
            },
            MuiFilledInput: {
                root: {
                    backgroundColor: rgba(ACCENT, 0.06),
                    borderRadius: 12,
                    '&$disabled': {
                        backgroundColor: rgba(ACCENT, 0.06),
                    },
                },
            },
            MuiTableCell: {
                head: {
                    backgroundColor: tableHeadBg,
                    color: tableHeadColor,
                    fontWeight: 700,
                    textTransform: 'uppercase' as const,
                    letterSpacing: '0.04em',
                    fontSize: '0.72rem',
                },
                body: {
                    borderBottom: `1px solid ${tableBodyBorder}`,
                },
            },
            MuiTableRow: {
                hover: {
                    backgroundColor: rgba(ACCENT, isDark ? 0.08 : 0.04),
                },
            },
            MuiTabs: {
                indicator: {
                    height: 3,
                    borderRadius: 999,
                },
            },
            MuiTab: {
                root: {
                    textTransform: 'none' as const,
                    fontWeight: 600,
                },
            },
            MuiLinearProgress: {
                colorPrimary: {
                    backgroundColor: isDark ? '#2a2f33' : '#e6e1db',
                },
                barColorPrimary: {
                    backgroundColor: isDark ? '#8a97a0' : '#aab6b0',
                },
            },
            MuiChip: {
                root: {
                    borderRadius: 999,
                    fontWeight: 600,
                },
            },
        } as any),
    };
};

// Sidebar CSS custom properties (consumed by App.css). Applied inline on the
// shell wrapper in Layout.tsx so the sidebar chrome tracks the active theme.
const buildCssVars = (spec: ThemeSpec): Record<string, string> => {
    const isDark = spec.mode === 'dark';
    return {
        '--app-sidebar-bg': spec.sidebarBg,
        '--app-sidebar-border': isDark ? 'rgba(255, 255, 255, 0.08)' : 'rgba(28, 30, 31, 0.06)',
        '--app-menu-header-bg': spec.menuHeaderBg,
        '--app-menu-header-border': rgba(ACCENT, isDark ? 0.3 : 0.14),
        '--app-menu-title': isDark ? BRAND_TITLE_DARK : BRAND_TITLE_LIGHT,
        '--app-menu-subtitle': isDark ? '#9aa2a6' : '#8a9194',
        '--app-menu-section': isDark ? '#8b9498' : '#8a8f92',
    };
};

const THEME_SPECS: ThemeSpec[] = [
    {
        name: 'light',
        label: 'Light',
        mode: 'light',
        bgDefault: '#f4f1eb',
        bgPaper: '#ffffff',
        bodyGradient:
            'radial-gradient(circle at 10% 10%, rgba(195, 200, 188, 0.35), transparent 45%), radial-gradient(circle at 90% 0%, rgba(207, 178, 146, 0.35), transparent 40%)',
        textPrimary: '#1c1e1f',
        textSecondary: '#4f5b60',
        sidebarBg: '#ffffff',
        menuHeaderBg:
            'linear-gradient(135deg, rgba(255, 255, 255, 0.98), rgba(255, 246, 250, 0.92))',
        appBarBg: 'rgba(255, 255, 255, 0.92)',
        secondary: '#c27a38',
        swatch: { bg: '#f4f1eb', surface: '#ffffff', accent: ACCENT },
    },
    {
        name: 'dark',
        label: 'Dark',
        mode: 'dark',
        bgDefault: '#121517',
        bgPaper: '#181c1f',
        bodyGradient:
            'radial-gradient(circle at 10% 10%, rgba(60, 70, 66, 0.35), transparent 45%), radial-gradient(circle at 90% 0%, rgba(82, 62, 48, 0.30), transparent 40%)',
        textPrimary: '#e8ebec',
        textSecondary: '#9aa8ac',
        sidebarBg: '#14181b',
        menuHeaderBg:
            'linear-gradient(135deg, rgba(38, 44, 48, 0.98), rgba(52, 33, 43, 0.92))',
        appBarBg: 'rgba(20, 24, 27, 0.92)',
        secondary: '#d9a066',
        swatch: { bg: '#181c1f', surface: '#14181b', accent: ACCENT },
    },
    {
        name: 'midnight',
        label: 'Midnight',
        mode: 'dark',
        bgDefault: '#0f1420',
        bgPaper: '#171d2e',
        bodyGradient:
            'radial-gradient(circle at 10% 10%, rgba(46, 54, 92, 0.4), transparent 45%), radial-gradient(circle at 90% 0%, rgba(70, 40, 80, 0.35), transparent 40%)',
        textPrimary: '#e6e9f2',
        textSecondary: '#99a3c0',
        sidebarBg: '#131826',
        menuHeaderBg:
            'linear-gradient(135deg, rgba(23, 29, 46, 0.98), rgba(40, 28, 52, 0.92))',
        appBarBg: 'rgba(15, 20, 32, 0.92)',
        secondary: '#6f8ed6',
        swatch: { bg: '#171d2e', surface: '#131826', accent: ACCENT },
    },
    {
        name: 'ocean',
        label: 'Ocean',
        mode: 'light',
        bgDefault: '#eef3f6',
        bgPaper: '#ffffff',
        bodyGradient:
            'radial-gradient(circle at 10% 10%, rgba(180, 205, 215, 0.4), transparent 45%), radial-gradient(circle at 90% 0%, rgba(150, 190, 210, 0.35), transparent 40%)',
        textPrimary: '#12252b',
        textSecondary: '#40575f',
        sidebarBg: '#f7fbfc',
        menuHeaderBg:
            'linear-gradient(135deg, rgba(255, 255, 255, 0.98), rgba(233, 244, 247, 0.92))',
        appBarBg: 'rgba(247, 251, 252, 0.92)',
        secondary: '#3f83bd',
        swatch: { bg: '#eef3f6', surface: '#f7fbfc', accent: ACCENT },
    },
    {
        name: 'sprive',
        label: 'Sprive Pink',
        mode: 'light',
        bgDefault: '#fbeef4',
        bgPaper: '#ffffff',
        bodyGradient:
            'radial-gradient(circle at 10% 10%, rgba(255, 205, 225, 0.4), transparent 45%), radial-gradient(circle at 90% 0%, rgba(245, 180, 205, 0.35), transparent 40%)',
        textPrimary: '#241019',
        textSecondary: '#6b4655',
        sidebarBg: '#fff6fa',
        menuHeaderBg:
            'linear-gradient(135deg, rgba(255, 255, 255, 0.98), rgba(253, 232, 242, 0.95))',
        appBarBg: 'rgba(255, 246, 250, 0.95)',
        secondary: '#c27a38',
        swatch: { bg: '#fbeef4', surface: '#fff6fa', accent: ACCENT },
    },
    {
        name: 'solarized',
        label: 'Solarized',
        mode: 'light',
        bgDefault: '#fdf6e3',
        bgPaper: '#fbf3df',
        bodyGradient:
            'radial-gradient(circle at 10% 10%, rgba(220, 210, 170, 0.4), transparent 45%), radial-gradient(circle at 90% 0%, rgba(210, 190, 150, 0.35), transparent 40%)',
        textPrimary: '#586e75',
        textSecondary: '#657b83',
        sidebarBg: '#f4ecd6',
        menuHeaderBg:
            'linear-gradient(135deg, rgba(253, 246, 227, 0.98), rgba(238, 232, 213, 0.95))',
        appBarBg: 'rgba(253, 246, 227, 0.95)',
        secondary: '#b58900',
        swatch: { bg: '#fdf6e3', surface: '#f4ecd6', accent: ACCENT },
    },
];

export interface ThemeDefinition {
    name: string;
    label: string;
    mode: ThemeMode;
    swatch: { bg: string; surface: string; accent: string };
    cssVars: Record<string, string>;
    theme: SpriveThemeOptions;
}

export const themeRegistry: ThemeDefinition[] = THEME_SPECS.map((spec) => ({
    name: spec.name,
    label: spec.label,
    mode: spec.mode,
    swatch: spec.swatch,
    cssVars: buildCssVars(spec),
    theme: buildTheme(spec),
}));

export const getThemeDefinition = (name: string): ThemeDefinition =>
    themeRegistry.find((t) => t.name === name) || themeRegistry[0];

// Back-compat exports (Login.tsx / any direct importers).
export const lightTheme = getThemeDefinition('light').theme;
export const darkTheme = getThemeDefinition('dark').theme;
